import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import {
  FILE_PREVIEW_KIND,
  FilePreviewModal,
  resolveFilePreviewKind,
} from "../../../ui";
import { fetchClientReportObjectUrl } from "./clientReportUpload";
import { formatClientReportDate } from "./clientReportMappers";

/**
 * Client-report preview: resolves a fresh download URL, fetches the file as an
 * object URL, and hands it to the shared <FilePreviewModal />. Header shows the
 * uploader and upload date/time; Download / Close stay available throughout.
 */
const ClientReportPreviewModal = ({
  open,
  report,
  labels,
  isRtl,
  downloading,
  resolveUrl,
  onDownload,
  onClose,
}) => {
  const [fileUrl, setFileUrl] = useState("");
  const [failed, setFailed] = useState(false);
  // Holds the live object URL so it is revoked only when replaced, never while
  // a child (image / PDF / sheet) is still reading from it.
  const urlRef = useRef("");

  const contentType = report?.raw?.content_type;
  const previewKind = report
    ? resolveFilePreviewKind(contentType, report.name)
    : FILE_PREVIEW_KIND.unsupported;
  const previewable =
    !!report && previewKind !== FILE_PREVIEW_KIND.unsupported;

  useEffect(() => {
    setFileUrl("");
    setFailed(false);

    // Non-renderable types show the note without a fetch.
    if (!open || !report || !previewable) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const url = await fetchClientReportObjectUrl(
          await resolveUrl(report),
          previewKind === FILE_PREVIEW_KIND.html ? "text/html" : undefined,
        );
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        setFileUrl(url);
      } catch (error) {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, report, previewable, previewKind, resolveUrl]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const dateText = report
    ? [formatClientReportDate(report.date), report.time].filter(Boolean).join(" ")
    : "";

  return (
    <FilePreviewModal
      open={open}
      fileName={report?.name || ""}
      contentType={contentType}
      meta={
        report ? (
          <>
            <bdi>{report.uploaderName || labels.unknownUploader}</bdi>
            {dateText ? (
              <>
                {" • "}
                <span dir="ltr">{dateText}</span>
              </>
            ) : null}
          </>
        ) : null
      }
      fileUrl={fileUrl}
      failed={failed}
      isRtl={isRtl}
      labels={labels}
      downloadDisabled={downloading}
      onDownload={() => report && onDownload(report)}
      onClose={onClose}
    />
  );
};

ClientReportPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  report: PropTypes.object,
  labels: PropTypes.object.isRequired,
  isRtl: PropTypes.bool,
  downloading: PropTypes.bool,
  /** async (report) => fresh download URL string */
  resolveUrl: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ClientReportPreviewModal.defaultProps = {
  report: null,
  isRtl: false,
  downloading: false,
};

export default ClientReportPreviewModal;
